import { ref, toRaw } from 'vue';
import { BodyFormModel } from '@/presentation/components/form/BodyFormModel';
import {
	EditorEvent,
	IEditorIntegration,
	IEditorValidationError,
} from '@studyportals/bodies-editor-integration';
import { OnMounted, OnUnmounted } from '@studyportals/vue-platform';
import { mediatedEventPublish } from '@/platform/core/MediatedEvents';
import { toBodyInfo } from '@/presentation/components/form/BodyFormModelExtensions';
import BodyService from '@/services/BodyService';
import { PlatformIntegration } from '@/platform/PlatformIntegration';
import { IFormOperations } from '@/presentation/components/form/IFormOperations';
import { EditorDirtyStateMonitor } from './EditorDirtyStateMonitor';

export class EditorCB implements OnMounted, OnUnmounted, IEditorIntegration {
	private readonly forms = new Array<IFormOperations>();
	private readonly dirtyStateMonitor: EditorDirtyStateMonitor;

	public readonly formModel = ref<BodyFormModel>();

	public lastErrorMessage = ref<string | undefined>();
	public isFormAvailable = ref<boolean>(false);

	public constructor() {
		this.dirtyStateMonitor = new EditorDirtyStateMonitor(this.formModel);
	}

	public mounted(): void {
		this.notifyLoaded();

		this.reset();
		this.isFormAvailable.value = true;

		this.notifyReady();
		this.dirtyStateMonitor.start();
	}

	public unmounted(): void {
		this.dirtyStateMonitor.stop();
	}

	public async validate(): Promise<readonly IEditorValidationError[]> {
		const errors: IEditorValidationError[] = [];

		for (const form of this.forms) {
			if (await form.validate()) {
				continue;
			}

			errors.push({ editorTab: form.tab });
		}

		return Promise.resolve(errors);
	}

	public getObjectNamePlural(): string {
		return 'Bodies';
	}

	public getObjectNameSingular(): string {
		return 'Body';
	}

	public getTitle(): string {
		return 'New body';
	}

	public getPublicUrl(): string {
		return '#';
	}

	public async save(): Promise<void> {
		const formModel = toRaw(this.formModel.value);
		if (formModel === undefined) {
			return;
		}

		try {
			const bodyInfo = toBodyInfo(formModel);
			await BodyService.create(bodyInfo);

			this.lastErrorMessage.value = undefined;

			this.reset();
			this.notifyFormClean();

			PlatformIntegration.instance.router.toOverview();
		} catch (error) {
			this.lastErrorMessage.value = (error as Error).message;

			throw error;
		}
	}

	public onFormReady(form: IFormOperations): void {
		this.forms.push(form);
	}

	private reset(): void {
		this.formModel.value = new BodyFormModel();
	}

	private notifyLoaded(): void {
		mediatedEventPublish<IEditorIntegration>(EditorEvent.LOADED, this);
	}

	private notifyReady(): void {
		mediatedEventPublish(EditorEvent.READY);
	}

	public notifyFormClean(): void {
		mediatedEventPublish(EditorEvent.FORM_CLEAN);
	}
}
